import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type LucideIcon } from 'lucide-react';
import { stripRequiredMarker, type DraftColumnDef } from './EditableDraftTable';

export interface ExplainerField {
  icon: ReactNode;
  labelKey: string;
  descriptionKey: string;
  required: boolean;
}

export interface ExplainerSection {
  titleKey?: string;
  fields: ExplainerField[];
}

/** The standard 34px icon tile content for an explainer field. */
export function explainerIcon(Icon: LucideIcon): ReactNode {
  return <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />;
}

/** The Abkürzung tile shows a mono hash instead of a lucide icon. */
export const hashIcon: ReactNode = <span className="font-mono text-[15px] font-semibold">#</span>;

/**
 * Derives a step's field explainer from its column defs: a column with an
 * `explainer` appears, its label and required-ness taken from the column so
 * table, template and explainer cannot drift.
 */
export function explainerFieldsFromColumns<TData>(
  columns: DraftColumnDef<TData>[]
): ExplainerField[] {
  return columns.flatMap((column) =>
    column.explainer
      ? [
          {
            icon: column.explainer.icon,
            labelKey: column.headerKey,
            descriptionKey: column.explainer.descriptionKey,
            required: !!column.required
          }
        ]
      : []
  );
}

function RequirednessBadge({ required }: { required: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      className={`rounded-[5px] px-1.75 py-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-[0.04em] ${
        required ? 'bg-info-solid/10 text-info' : 'bg-muted text-muted-foreground'
      }`}
    >
      {t(
        required ? 'initialSetup.fieldExplainer.required' : 'initialSetup.fieldExplainer.optional'
      )}
    </span>
  );
}

/** The per-column explainer list shared by the empty state and the collapsible card. */
export function FieldExplainer({ sections }: { sections: ExplainerSection[] }) {
  const { t } = useTranslation();

  return (
    <div className="max-w-[760px]">
      {sections.map((section, sectionIndex) => (
        <div key={section.titleKey ?? sectionIndex}>
          {section.titleKey && (
            <h4 className="mt-5 text-sm font-semibold italic text-foreground">
              {t(section.titleKey)}
            </h4>
          )}
          <div className="mt-4 flex flex-col gap-4">
            {section.fields.map((field) => (
              <div key={field.labelKey} className="flex gap-3">
                <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] bg-muted text-muted-foreground">
                  {field.icon}
                </span>
                <div>
                  <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                    {stripRequiredMarker(t(field.labelKey))}
                    <RequirednessBadge required={field.required} />
                  </div>
                  <p className="mt-[3px] text-[13px] leading-[1.5] text-fg-secondary">
                    {t(field.descriptionKey)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
